/* eslint-disable no-console */

// Post-unlock passkey offer (2026-09-30). When this browser has just come to
// hold the account identity WITHOUT a passkey of its own — the user loaded or
// typed the recovery code, or the keys arrived from another browser / device
// through "Share encryption keys" — offer to set one up here, so the next
// unlock is a screen-lock touch instead of the code or another device.
//
// The rule is one WORKING passkey per browser (user decision 2026-09-30,
// relaxing the 2026-08-13 "one passkey set per account" rule): the offer is
// skipped only when this browser's local marker (Keystore.recordLocalPasskey)
// names a credential the published bundle still carries. A marker whose wrap
// was revoked does not block.
//
// Same shape as Nc1UnlockPrompt: callers `request`, the one host mounted in
// App.vue (Nc1PasskeyOfferDialog.vue) subscribes via `onRequest` and runs the
// consent dialog + ceremony. Once per account per page load whatever the
// outcome, and only ever for the SIGNED-IN user's own identity.

import store from '@/store'
import E2eeApi from '@/aws/e2ee/E2eeApi'
import * as Keystore from './Keystore'
import { prfProbablySupported } from './PasskeyPrf'
import { passkeyMode } from './PasskeyPolicy'
import { sameAccount } from '../E2EEPasscodePrompt'

// Accounts already offered in this page load (skipped, enrolled or failed all
// count): a second recovery unlock in one load only follows a rotation, and
// the two watchers that can notice the same link grant must not both ask.
const offered = new Set()

/**
 * Can this browser add its own passkey for `email` right now? Returns null
 * when not (no E2EE API, no platform authenticator, no cached identity, or a
 * working passkey already registered from this browser), else what the host
 * needs: the cached identity and whether the consent dialog may be skipped
 * (`required` mirrors the login catch-up — only when the account has NO
 * passkey at all under VUE_APP_E2EE_PASSKEY_MODE=required).
 * Never throws: an offer must not spoil the unlock that led to it.
 */
export async function passkeyOfferEligible (email) {
  try {
    if (!E2eeApi.isConfigured()) return null
    if (!(await prfProbablySupported())) return null
    const cached = await Keystore.loadIdentity(email)
    if (!cached || !cached.secrets) return null
    const bundle = (await E2eeApi.getIdentityBundle(email)).data
    const wraps = (bundle && bundle.wraps && bundle.wraps.webauthn) || []
    const local = await Keystore.getLocalPasskey(email)
    if (local && wraps.some((w) => w.credentialId === local.credentialIdB64)) {
      console.log('[nc1] passkey offer skipped: this browser already has a working passkey')
      return null
    }
    return { cached, required: passkeyMode() === 'required' && wraps.length === 0 }
  } catch (e) {
    console.log('[nc1] passkey offer eligibility check failed', e)
    return null
  }
}

class Nc1PasskeyOfferController {
  constructor () {
    this.onRequest = null
    this.pending = new Set()
    this.enrolledListeners = new Set()
  }

  /**
   * opts.reason: 'recovery' (the recovery code unlocked this browser) or
   * 'link' (the keys arrived from another browser / device). It picks the
   * dialog's intro only; the eligibility rule is the same for both.
   */
  request (email, opts = {}) {
    if (!email) return
    const key = String(email).toLowerCase()
    // The passkey wraps THIS user's identity: never offer for an account the
    // user merely views, however the unlock was requested.
    if (!sameAccount(key, store.state.account && store.state.account.accountId)) {
      console.log('[nc1] passkey offer skipped for ' + key + ': not the signed-in account')
      return
    }
    if (offered.has(key) || this.pending.has(key)) return
    offered.add(key)
    this.pending.add(key)
    console.log('[nc1] passkey offer requested for ' + key + ' (' + (opts.reason || 'unlock') + ')')
    if (this.onRequest) {
      try { this.onRequest(key, opts) } catch (e) { console.log(e) }
    }
  }

  resolve (email) {
    if (email) this.pending.delete(String(email).toLowerCase())
  }

  // The Account page lists the passkeys: it refreshes when the offer enrolled
  // one while the page was open. Returns an unsubscribe function.
  onEnrolled (listener) {
    this.enrolledListeners.add(listener)
    return () => this.enrolledListeners.delete(listener)
  }

  notifyEnrolled (email) {
    for (const listener of this.enrolledListeners) {
      try { listener(email) } catch (e) { console.log(e) }
    }
  }
}

export const Nc1PasskeyOffer = new Nc1PasskeyOfferController()
